
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Download, Brain, Heart, Target, BookOpen, Users, Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface YoungSchemasAdvancedAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: string;
  insightId: string;
}

export default function YoungSchemasAdvancedAnalysisModal({
  isOpen,
  onClose,
  analysis,
  insightId
}: YoungSchemasAdvancedAnalysisModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadAnalysis = () => {
    setIsDownloading(true);
    
    const element = document.createElement('a');
    const file = new Blob([analysis], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `analise-esquemas-young-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    setTimeout(() => setIsDownloading(false), 1000);
  };

  // Parse das seções da análise
  const parseAnalysis = (text: string) => {
    const sections = text.split('##').filter(section => section.trim());
    return sections.map(section => {
      const lines = section.trim().split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      return { title, content };
    });
  };

  const sections = parseAnalysis(analysis);

  const getSectionIcon = (title: string) => {
    if (title.includes('PERFIL') || title.includes('GERAL')) return Brain;
    if (title.includes('DOMÍNIOS')) return Target;
    if (title.includes('ESQUEMAS') || title.includes('CENTRAIS')) return Heart;
    if (title.includes('TERAPÊUTICAS') || title.includes('ESTRATÉGIAS')) return Lightbulb;
    if (title.includes('DESENVOLVIMENTO') || title.includes('PLANO')) return BookOpen;
    if (title.includes('RELACIONAMENTOS')) return Users;
    return Brain;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Brain className="h-6 w-6 text-purple-600" />
            <span>Análise Avançada - Esquemas de Young</span>
            <Badge variant="outline" className="ml-2">IA</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">
            Análise personalizada baseada na Terapia do Esquema de Jeffrey Young
          </p>
          <Button
            onClick={downloadAnalysis}
            disabled={isDownloading}
            variant="outline"
            size="sm"
          >
            <Download className="mr-2 h-4 w-4" />
            {isDownloading ? 'Baixando...' : 'Baixar'}
          </Button>
        </div>

        <ScrollArea className="max-h-[60vh] w-full">
          <div className="space-y-6">
            <AnimatePresence>
              {sections.map((section, index) => {
                const Icon = getSectionIcon(section.title);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center space-x-2 text-lg">
                          <Icon className="h-5 w-5 text-purple-600" />
                          <span>{section.title}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm max-w-none">
                          {section.content.split('\n').map((paragraph, pIndex) => {
                            if (paragraph.trim().startsWith('-')) {
                              return (
                                <div key={pIndex} className="flex items-start space-x-2 mb-2">
                                  <span className="text-purple-600 mt-1">•</span>
                                  <span className="text-sm">{paragraph.substring(1).trim()}</span>
                                </div>
                              );
                            } else if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                              return (
                                <h4 key={pIndex} className="font-semibold text-sm mt-4 mb-2 text-purple-700">
                                  {paragraph.replace(/\*\*/g, '')}
                                </h4>
                              );
                            } else if (paragraph.trim()) {
                              return (
                                <p key={pIndex} className="text-sm mb-3 leading-relaxed">
                                  {paragraph}
                                </p>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </CardContent>
                    </Card>
                    {index < sections.length - 1 && <Separator className="my-4" />}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </ScrollArea>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Brain className="h-4 w-4" />
            <span>Análise ID: {insightId.slice(0, 8)}</span>
            <span>•</span>
            <span>Gerado em {new Date().toLocaleDateString('pt-BR')}</span>
          </div>
          <Button onClick={onClose} variant="outline">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
